import { omit, pick } from '@/shared/utils/object';
import {
  AfterSaleAnalysisVo,
  AfterSaleDetailDifferencePageVo,
  AfterSaleDetailSummaryPageVo,
  AfterSaleDetailSummaryReq,
  AfterSaleDifferenceReq,
  BusinessAnalysisInfoVo,
  BusinessSummaryReq,
  BusinessSummaryVo,
  BusinessTypeVo,
  DemandStockRegisterDetailVo,
  DemandStockRegisterInfoQueryReq,
  DemandSummaryReq,
  DemandSummaryVo,
  FinancialSummaryInfoVo,
  FinancialSummaryReq,
  FinancialSummaryStatisticReq,
  FinancialSummaryVo,
  InquiryInfoReq,
  InquiryInfoVo,
  InspectionStatsDemandReq,
  InspectionStatsResultDemandReq,
  InspectionStatsResultSupplierReq,
  InspectionStatsSupplierReq,
  InspectionTaskResultListVo,
  ProjectAddReq,
  ProjectAttachmentUpdateReq,
  ProjectBatchReq,
  ProjectDetailVo,
  ProjectEvalDetailVo,
  ProjectInfoVo,
  ProjectListVo,
  ProjectPactReq,
  ProjectQueryReq,
  ProjectSelectReq,
  ProjectSummaryVo,
  ProjectUpdateReq,
  PurchaseAnalysisVo,
  PurchaseDetailDemandReq,
  PurchaseDetailSupplierReq,
  PurchaseDifferenceListVo,
  PurchaseDifferenceSearchReq,
  PurchaseSummaryByCanteenReq,
  PurchaseSummaryByCanteenVo,
  PurchaseSummaryByDemandVo,
  PurchaseSummaryByProductVo,
  PurchaseSummaryBySupplierVo,
  PurchaseSummaryDetailByDemand,
  PurchaseSummaryDetailBySupplier,
  PurchaseSummaryEvalVoByDateVo,
  PurchaseSummaryEvalVoByDemandVo,
  PurchaseSummaryEvalVoBySupplierVo,
  PurchaseSummaryProductDetailReq,
  PurchaseSummaryProductDetailVo,
  QuotationImportSegmentReq,
  RevenueStatisticsReq,
  RevenueStatisticsResultVo,
  SegmentEvalReq,
  StatementDetailsDocumentsGroupingListVo,
  StatementDetailsProductCategoryGroupingListVo,
  StatementDetailsProductCroupListVo,
  StatementDetailsSearchReq,
  StatementSummaryQueryReq,
  StatementSummaryVo,
  SummaryQueryByDate,
  SummaryQueryByDemand,
  SummaryQueryBySupplier,
} from 'typings/openapi';
import { request } from '../http';
import { ReportDateTypeEnum } from './constants';
import {
  GetManagePurchaseReportPurchaseListRequest,
  GetManagePurchaseReportSkuListRequest,
  GetManagePurchaseReportSupplyListRequest,
  GetPurchaserReportSkuListRequest,
  GetPurchaserReportSupplyListRequest,
  GetSupplyPurchaseReportDemandListRequest,
  GetSupplyPurchaseReportSkuListRequest,
} from './types';

/**
 * 供方采购汇总商品
 * @param data
 * @returns
 */
export const GetSupplyPurchaseReportSkuList = (data: GetSupplyPurchaseReportSkuListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByProductVo>>('/purchaseSummary/supplier/byProduct', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};
/**
 * 供方采购汇总需方
 * @param data
 * @returns
 */
export const GetSupplyPurchaseReportDemandList = (data: GetSupplyPurchaseReportDemandListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByDemandVo>>('/purchaseSummary/supplier/byDemand', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 供方采购汇总表 --- 按食堂
 */
export const GetSupplyPurchaseReportDiningList = (
  data: PurchaseSummaryByCanteenReq & { pageSize: number; pageNum: number },
) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByCanteenVo>>('/purchaseSummary/supplier/byCanteen', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 需方采购汇总商品
 * @param data
 * @returns
 */
export const GetPurchaserPurchaseReportSkuList = (data: GetPurchaserReportSkuListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByProductVo>>('/purchaseSummary/demand/byProduct', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 需方采购汇总供方
 * @param data
 * @returns
 */
export const GetPurchaserPurchaseReportSupplyList = (data: GetPurchaserReportSupplyListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryBySupplierVo>>('/purchaseSummary/demand/bySupplier', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 需方采购汇总表 --- 按食堂 */
export const GetPurchaserPurchaseReportDiningList = (
  data: PurchaseSummaryByCanteenReq & { pageSize: number; pageNum: number },
) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByCanteenVo>>('/purchaseSummary/demand/byCanteen', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 采购明细 */
export const GetPurchaserPurchaseReportDetailList = (data: PurchaseSummaryProductDetailReq) => {
  return request<PagingResponse<PurchaseSummaryProductDetailVo>>('/purchaseSummary/product/detail/purchase/summary', {
    method: 'POST',
    data,
  });
};

/**
 * 监管平台采购汇总商品
 * @param data
 * @returns
 */
export const GetManagePurchaseReportSkuList = (data: GetManagePurchaseReportSkuListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByProductVo>>('/purchaseSummary/generating/byProduct', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 监管平台采购汇总需方
 * @param data
 * @returns
 */
export const GetManagePurchaseReportPurchaseList = (data: GetManagePurchaseReportPurchaseListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByDemandVo>>('/purchaseSummary/generating/byDemand', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/**
 * 监管平台采购汇总供方
 * @param data
 * @returns
 */
export const GetManagePurchaseReportSupplyList = (data: GetManagePurchaseReportSupplyListRequest) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryBySupplierVo>>('/purchaseSummary/generating/bySupplier', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 监管平台采购汇总表 --- 按食堂 */
export const GetManagePurchaseReportDiningList = (
  data: PurchaseSummaryByCanteenReq & { pageSize: number; pageNum: number },
) => {
  const _params = pick(data, ['pageSize', 'pageNum']);
  const _data = omit(data, ['pageSize', 'pageNum']);
  return request<PagingResponse<PurchaseSummaryByCanteenVo>>('/purchaseSummary/generating/byCanteen', {
    method: 'POST',
    data: _data,
    params: _params,
  });
};

/** 供方-经营分析 */
export const GetSupplyBusinessAnalysisList = (date: ReportDateTypeEnum) => {
  return request<BusinessAnalysisInfoVo>(`/purchaseSummary/business/analysis/${date}`, {
    method: 'POST',
  });
};

/** 财务汇总表-按日期 */
export const GetFinancialSummaryByDayList = (data: RevenueStatisticsReq) => {
  return request<RevenueStatisticsResultVo>('/revenue/statisticsResultByDay', {
    method: 'POST',
    data,
  });
};

/** 财务汇总表-按月份 */
export const GetFinancialSummaryByMonthList = (data: RevenueStatisticsReq) => {
  return request<RevenueStatisticsResultVo>('/revenue/statisticsResultByMonth', {
    method: 'POST',
    data,
  });
};

/** 监管方财务汇总表 */
export const GetManageFinancialSummaryList = (data: FinancialSummaryReq) => {
  return request<FinancialSummaryVo[]>('/financialSummary/supervisor/queryList', {
    method: 'POST',
    data,
  });
};

/** 询价公示列表 */
export const GetInquiryPublicList = (data: InquiryInfoReq) =>
  request<PagingResponse<InquiryInfoVo>>('/inquiryInfo/inquiryPublicity/list', { method: 'POST', data });

/** 项目公示列表 */
export const GetProjectPublicList = (data: ProjectQueryReq) =>
  request<PagingResponse<ProjectSummaryVo[]>>('/project/summary', { method: 'POST', data });

/** 项目列表 */
export const GetProjectList = (data: ProjectQueryReq) =>
  request<PagingResponse<ProjectListVo[]>>('/project/queryList', { method: 'POST', data });

/** 新建项目 */
export const AddProject = (data: ProjectAddReq) => request('/project/add', { method: 'POST', data });

/** 编辑项目 */
export const EditProject = (data: ProjectUpdateReq) => request('/project/update', { method: 'POST', data });

/** 项目详情 */
export const GetProjectDetail = (projectId: string) =>
  request<ProjectDetailVo>(`/project/detail/${projectId}`, { method: 'GET' });

/** 项目详情-通过单号获取 */
export const GetProjectDetailByNo = (projectNo: string) =>
  request<ProjectDetailVo>(`/project/getProjectDetailByNo/${projectNo}`, { method: 'GET' });

/** 修改项目附件 */
export const UpdateProjectAttachment = (data: ProjectAttachmentUpdateReq) =>
  request('/project/update/attachmentList', { method: 'POST', data });

/** 项目公示详情-带供方信息 */
export const GetProjectDetailWithSupply = (projectId: string) =>
  request<ProjectEvalDetailVo>(`/project/eval/${projectId}`, { method: 'GET' });

/** 项目公示详情-带供方信息，通过项目编号查询 */
export const GetProjectDetailWithSupplyByNo = (projectNo: string) =>
  request<ProjectEvalDetailVo>(`/project/eval/byNo/${projectNo}`, { method: 'GET' });

/** 批量更新项目状态 (发布、公示、取消公示、关闭、删除) */
export const BatchUpdateProjectStatus = (data: ProjectBatchReq) =>
  request('/project/batchUpdateStatus', { method: 'POST', data });

/** 评标 */
export const UpdateSegmentEval = (data: SegmentEvalReq) =>
  request('/project/updateSegmentEval', { method: 'POST', data });

/** 修改关联报价单 失效/恢复 操作 */
export const UpdateOrderNoStatus = (data: SegmentEvalReq) =>
  request('/project/updateSegmentEval/flag', { method: 'POST', data });

/** 定标 --批量生成采购合同 */
export const BatchCreatePact = (data: ProjectPactReq) => request('/project/create/pact', { method: 'POST', data });

/** 下拉选择项目以及标段 */
export const GetProjectListWithSegment = (data: ProjectSelectReq) =>
  request<PagingResponse<ProjectInfoVo[]>>('/project/select', { method: 'POST', data });

/** 导入标段商品 */
export const ImportSegmentProduct = (data: QuotationImportSegmentReq) =>
  request('/quotation/importSegmentProduct', { method: 'POST', data });

/** 食安统计表 */
export const GetFoodSafetyReportList = (data: BusinessSummaryReq) =>
  request<PagingResponse<BusinessSummaryVo[]>>('/purchaseSummary/request/business/summary', { method: 'POST', data });

/** 食安统计业务列表 */
export const GetFoodSafeBusinessTypeList = () =>
  request<BusinessTypeVo[]>('/purchaseSummary/request/business/type/list', { method: 'GET' });

/** 需方---采购明细表 */
export const GetPurchaseDetailList = (data: PurchaseDetailDemandReq) =>
  request<PagingResponse<PurchaseSummaryDetailByDemand>>('/purchase/detail/demand/list', { method: 'POST', data });

/** 供方---采购明细表 */
export const GetSupplyPurchaseDetailList = (data: PurchaseDetailSupplierReq) =>
  request<PagingResponse<PurchaseSummaryDetailBySupplier>>('/purchase/detail/supplier/list', { method: 'POST', data });

/** 需方查询库存明细 */
export const GetPurchaseStockDetailList = (data: DemandStockRegisterInfoQueryReq) =>
  request<PagingResponse<DemandStockRegisterDetailVo>>('/purchase/detail/demand/register', { method: 'POST', data });

/** 查询需方组汇总 */
export const GetPurchaseGroupSummaryList = (data: DemandSummaryReq) =>
  request<PagingResponse<DemandSummaryVo>>('/demandSummary/supervisor/queryList', { method: 'POST', data });

/** 经营分析汇总 v3.4.0 */
export const GetManageOperationAnalysisList = (data: FinancialSummaryStatisticReq) => {
  return request<FinancialSummaryInfoVo>('/financialSummary/supervisor/business/queryList', {
    method: 'POST',
    data,
  });
};

/** 对账汇总列表 */
export const GetStatementSummaryList = (data: StatementSummaryQueryReq) =>
  request<PagingResponse<StatementSummaryVo>>('/statementSummary/list', { method: 'POST', data });

/** 采购差异表 */
export const GetPurchaseDiffReport = (data: PurchaseDifferenceSearchReq) =>
  request<PagingResponse<PurchaseDifferenceListVo>>('/purchaseDifference/list', { method: 'POST', data });

/** 业务分析采购单（月度） */
export const GetBusinessAnalysisPurchaseMonth = (params: { date: string }) =>
  request<PurchaseAnalysisVo>('/business/analysis/purchase/month', { method: 'GET', params });

/** 业务分析采购单（年度） */
export const GetBusinessAnalysisPurchaseYear = (params: { date: string }) =>
  request<PurchaseAnalysisVo>('/business/analysis/purchase/year', { method: 'GET', params });

/** 业务分析售后（月度） */
export const GetBusinessAnalysisAfterSalesMonth = (params: { date: string }) =>
  request<AfterSaleAnalysisVo>('/business/analysis/afterSale/month', { method: 'GET', params });

/** 业务分析售后（年度） */
export const GetBusinessAnalysisAfterSalesYear = (params: { date: string }) =>
  request<AfterSaleAnalysisVo>('/business/analysis/afterSale/year', { method: 'GET', params });

/** 巡检汇总表 ---按需方统计 v3.4.0 */
export const GetInspectionSummaryListByDemand = (data: InspectionStatsDemandReq) =>
  request('/inspection/statistics/by/demand', { method: 'POST', data });

/** 巡检汇总表 ---按供方统计 v3.4.0 */
export const GetInspectionSummaryListBySupply = (data: InspectionStatsSupplierReq) =>
  request('/inspection/statistics/by/supplier', { method: 'POST', data });

/** 展示当前「巡检任务模板」的「巡检结果」--按需方 v3.4.0 */
export const GetInspectionResByDemand = (data: InspectionStatsResultDemandReq) =>
  request<InspectionTaskResultListVo[]>('/inspection/task/statistics/demand/result', { method: 'POST', data });

/** 展示当前「巡检任务模板」的「巡检结果」--按供方 v3.4.0 */
export const GetInspectionResBySupply = (data: InspectionStatsResultSupplierReq) =>
  request('/inspection/task/statistics/supplier/result', { method: 'POST', data });

/** 展示当前「是否完成」的「整改记录」--按需方 v3.4.0 */
export const GetModifyRecordIsFinishedByDemand = (data: InspectionStatsResultDemandReq) =>
  request('/inspection/task/history/demand/result', { method: 'POST', data });

/** 展示当前「是否完成」的「整改记录」。--按供方 v3.4.0 */
export const GetModifyRecordIsFinishedBySupply = (data: InspectionStatsResultSupplierReq) =>
  request('/inspection/task/history/supplier/result', { method: 'POST', data });

/** 监管平台 -- 按日期 */
export const GetManagePurchaseSummaryEvalByDate = (data: SummaryQueryByDate) =>
  request<PagingResponse<PurchaseSummaryEvalVoByDateVo>>(
    '/purchaseSummary/eval/generating/queryPurchaseSummaryEvalByDate',
    { method: 'POST', data },
  );
/** 监管平台 -- 按需方 */
export const GetManagePurchaseSummaryEvalByDemand = (data: SummaryQueryByDemand) =>
  request<PagingResponse<PurchaseSummaryEvalVoByDemandVo>>(
    '/purchaseSummary/eval/generating/queryPurchaseSummaryEvalByDemand',
    { method: 'POST', data },
  );
/** 监管平台 -- 按供方 */
export const GetManagePurchaseSummaryEvalBySupplier = (data: SummaryQueryBySupplier) =>
  request<PagingResponse<PurchaseSummaryEvalVoBySupplierVo>>(
    '/purchaseSummary/eval/generating/queryPurchaseSummaryEvalBySupplier',
    { method: 'POST', data },
  );
/** 需方管理平台 -- 按日期 */
export const GetPurchaserPurchaseSummaryEvalByDate = (data: SummaryQueryByDate) =>
  request<PagingResponse<PurchaseSummaryEvalVoByDateVo>>(
    '/purchaseSummary/eval/demand/queryPurchaseSummaryEvalByDate',
    { method: 'POST', data },
  );
/** 需方管理平台 -- 按供方 */
export const GetPurchaserPurchaseSummaryEvalBySupplier = (data: SummaryQueryBySupplier) =>
  request<PagingResponse<PurchaseSummaryEvalVoBySupplierVo>>(
    '/purchaseSummary/eval/demand/queryPurchaseSummaryEvalBySupplier',
    { method: 'POST', data },
  );

/** 供方管理平台 -- 按日期 */
export const GetSupplyPurchaseSummaryEvalByDate = (data: SummaryQueryByDate) =>
  request<PagingResponse<PurchaseSummaryEvalVoByDateVo>>(
    '/purchaseSummary/eval/supplier/queryPurchaseSummaryEvalByDate',
    { method: 'POST', data },
  );
/** 供方管理平台 -- 按需方 */
export const GetSupplyPurchaseSummaryEvalByDemand = (data: SummaryQueryByDemand) =>
  request<PagingResponse<PurchaseSummaryEvalVoByDemandVo>>(
    '/purchaseSummary/eval/supplier/queryPurchaseSummaryEvalByDemand',
    { method: 'POST', data },
  );

/** 售后明细表 -- 需方 */
export const GetPurchaseAfterSaleDetailList = (data: AfterSaleDetailSummaryReq) =>
  request<PagingResponse<AfterSaleDetailSummaryPageVo>>('/afterSale/detail/summary/demand/page', {
    method: 'POST',
    data,
  });

/** 售后明细表 -- 供方 */
export const GetSupplyAfterSaleDetailList = (data: AfterSaleDetailSummaryReq) =>
  request<PagingResponse<AfterSaleDetailSummaryPageVo>>('/afterSale/detail/summary/supplier/page', {
    method: 'POST',
    data,
  });

/** 监管方售后差异表 */
export const GetAfterSaleDiffList = (data: AfterSaleDifferenceReq) =>
  request<PagingResponse<AfterSaleDetailDifferencePageVo>>('/afterSale/difference/query', { method: 'POST', data });

/** 对账明细列表（按商品） */
export const GetStatementDetailListByProduct = (data: StatementDetailsSearchReq) =>
  request<PagingResponse<StatementDetailsProductCroupListVo>>('/statementDetails/productGroupingList', {
    method: 'POST',
    data,
  });

/** 对账明细列表（按分类） */
export const GetStatementDetailListByCategory = (data: StatementDetailsSearchReq) =>
  request<PagingResponse<StatementDetailsProductCategoryGroupingListVo>>(
    '/statementDetails/productCategoryGroupingList',
    {
      method: 'POST',
      data,
    },
  );

/** 对账明细列表（按单据） */
export const GetStatementDetailListByBill = (data: StatementDetailsSearchReq) =>
  request<PagingResponse<StatementDetailsDocumentsGroupingListVo>>('/statementDetails/documentsGroupingList', {
    method: 'POST',
    data,
  });

/** 通过对账单单号获取商品明细 */
export const GetProductDetailListByStatementNo = (data: StatementDetailsSearchReq) =>
  request<StatementDetailsProductCroupListVo[]>('/statementDetails/noQueryDetails', {
    method: 'POST',
    data,
  });
